import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import compose from 'helpers/compose';

import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CircularProgress } from '@material-ui/core';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import AssessmentIcon from '@material-ui/icons/Assessment';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import FindInPageOutlinedIcon from '@material-ui/icons/FindInPageOutlined';
import Tab from '@material-ui/core/Tab';
import SystemUpdateIcon from '@material-ui/icons/SystemUpdate';
import SettingsIcon from '@material-ui/icons/Settings';
import ErrorIcon from '@material-ui/icons/Error';

import Tooltip from 'components/Tooltip/Tooltip';
import { sqlDateToLabel } from 'helpers/helpers';
import Tabs from 'components/CustomTabs/CustomTabs';
import MaterialTable from 'components/Table/Table';
import CustomPaper from 'components/Layout/Background';
import Message, { MessageVariant } from 'components/Message/Message';

import { RootStore } from 'stores/rootStore';
import { ReportsStore, Document, DocumentCategory, DocumentStatus, DocumentType } from 'stores/account/reportsStore';
import { Statement } from 'apps/Pdf/templates/Statement/Statement';
import PdfPreview from 'components/PdfPreview/PdfPreview';
import { EdwardColors } from 'enums';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    type: {
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        color: theme.palette.grey[600],
        marginRight: theme.spacing(1),
      },
    },
    action: {
      margin: theme.spacing(0, 1, 1, 0),
      '@media (max-width:1024px)': {
        margin: theme.spacing(0.5, 0),
        width: '100%',
      },
    },
    download: {
      minWidth: '251px',
      '@media (max-width:1024px)': {
        width: '100%',
        minWidth: 'auto',
      },
      '@media (max-width:400px)': {
        height: '32px',
        minWidth: '32px',
        width: '32px',
        padding: 0,
        '& .MuiButton-startIcon': {
          margin: 0,
          '& .MuiSvgIcon-root': {
            width: '1.5em',
            height: '1.5em',
          },
        },
      },
    },
    generate: {
      minWidth: '251px',
      '@media (max-width:1024px)': {
        width: '100%',
        minWidth: 'auto',
      },
    },
    dialogContent: {
      order: 1,
      margin: 0,
      maxWidth: '100%',
      '@media (max-width:1024px)': {
        padding: '0 !important',
      },
    },
    alert: {
      //margin: theme.spacing(1)
      alignSelf: 'center',
    },
    table: {
      '& thead th:first-child': { width: `0 !important`, padding: '0 !important' },
      '& tbody td': { cursor: 'default !important' },
      '& tbody tr[index] td:first-child > div': { display: 'none !important' },
      '& tbody tr:not([index]):hover': { backgroundColor: 'transparent' },
      '& tbody tr:not([index]) td': { borderBottom: 'none' },
      '& tbody tr:not([index]) td > div': { marginTop: '-12px', transition: 'none !important' },
    },
  }),
);

interface Props {
  rootStore: RootStore;
  accountId: string;
}

const Reports: React.FC<Props> = ({ rootStore, accountId }) => {
  const { t } = useTranslation();
  const [store] = useState(new ReportsStore(rootStore, accountId));
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openDocument, setOpenDocument] = useState<Document | null>(null);
  const mobile = useMediaQuery('(max-width:400px)', { noSsr: true });

  const openPreview = (document: Document) => {
    setOpenDocument(document);
    setDialogOpen(true);
    rootStore.stores.uiStore.startLoading('pdfPreview', t('general_generatingReport'));
  };

  useEffect(() => {
    store.fetchData();
    return function cleanup() {
      store.clearTimer();
    };
  }, [store]);

  const tableRef = React.createRef<any>();

  useEffect(() => {
    if (tableRef?.current) {
      tableRef.current.state.data.forEach((data: any) => {
        if (!data.tableData.showDetailPanel && data.request?.waitingTooLong) {
          const msg = document.getElementById(`report_${data.id}`);
          if (msg) msg.click();
        }
      });
    }
  }, [tableRef, store.data.reports]);

  useEffect(() => {
    if (store.data.reports.length && store.data.summaries.length && !store.copied) {
      store.copySummaries();
    }
  }, [store, store.data.reports, store.data.summaries, store.copied]);

  const [tab, setTab] = useState(DocumentCategory.REPORTS);

  // value?: number should be value: number, but the @material-ui typing is bugged
  const handleTabChange = (_event: React.ChangeEvent<{}>, value?: DocumentCategory) => {
    if (value !== undefined) {
      setTab(value);
    }
  };

  return (
    <Container maxWidth="md">
      <CustomPaper>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value={DocumentCategory.REPORTS} label={t('accountDetail_reports')} />
          <Tab value={DocumentCategory.SUMMARIES} label={t('accountDetail_summaries')} />
        </Tabs>

        {(store.data.summaries.length > 0 || store.data.reports.length > 0) && (
          <MaterialTable
            tableRef={tableRef}
            className={classes.table}
            options={{
              header: true,
              sorting: true,
              draggable: false,
              search: false,
              toolbar: false,
              emptyRowsWhenPaging: false,

              pageSizeOptions: [13, 50, 100],
              paging:
                (tab === DocumentCategory.SUMMARIES && store.data.summaries.length > 13) ||
                (tab === DocumentCategory.REPORTS && store.data.reports.length > 13),
              //paging: true,
              pageSize: 13,

              //paging: false
              rowStyle: (rowData) => ({
                //paddingBottom: rowData.request?.waitingTooLong ? '#EEE' : '#FFF',
              }),
            }}
            quickfix
            data={
              //store.data.filter((doc) => ReportCategories[tab].includes(doc.type))
              tab === DocumentCategory.SUMMARIES ? store.data.summaries : store.data.reports
            }
            columns={[
              {
                title: 'Typ',
                field: 'type',
                render: ({ type, id }) => (
                  <div className={classes.type} id={`report_${id}`}>
                    {type === DocumentType.MONTH_SUMMARY || type === DocumentType.MONTH ? (
                      <AssessmentOutlinedIcon />
                    ) : (
                      <AssessmentIcon />
                    )}{' '}
                    {t(`pdf_document_${type}`)}
                  </div>
                ),
              },
              {
                title: 'Datum',
                field: 'date',
                defaultSort: 'desc',
                render: ({ date }) => t(`${sqlDateToLabel(date, 'dd. MM. yyyy')}`),
                cellStyle: {
                  whiteSpace: 'nowrap',
                },
              },
              {
                field: 'template',
                render: (document: Document) => (
                  <>
                    {
                      /*
                    document.type === 'TAX' ||
                    document.type === 'LTIP_SUMMARY' ||
                    document.type === 'MONTH_SUMMARY' ||
                    document.type === 'YEAR_SUMMARY'
                    */
                      tab === DocumentCategory.SUMMARIES ||
                      document.type === 'TAX' ||
                      document.type === 'LTIP_SUMMARY' ? (
                        <Button
                          variant={mobile ? 'text' : 'outlined'}
                          color={mobile ? 'primary' : 'default'}
                          className={classes.download}
                          startIcon={
                            document.loading ? (
                              <CircularProgress size={20} />
                            ) : mobile ? (
                              <SystemUpdateIcon />
                            ) : (
                              <GetAppIcon />
                            )
                          }
                          onClick={() => store.downloadOcpDocument(document)}
                          disabled={document.loading}
                        >
                          {!mobile && t('general_download')}
                        </Button>
                      ) : !document.request ||
                        document.request.status === DocumentStatus.WAITING ||
                        document.request.status === DocumentStatus.FAILED ? (
                        <Button
                          variant={mobile ? 'text' : 'contained'}
                          color={'primary'}
                          className={classes.generate}
                          startIcon={
                            document.request?.status === DocumentStatus.WAITING ? (
                              <CircularProgress size={20} color={'inherit'} />
                            ) : (
                              <SettingsIcon />
                            )
                          }
                          onClick={() => {
                            store.generateDocument(document);
                          }}
                          disabled={document.request?.status === DocumentStatus.WAITING}
                        >
                          {!mobile &&
                            t(
                              document.request?.status === DocumentStatus.WAITING
                                ? 'general_generating'
                                : 'general_generate',
                            )}
                        </Button>
                      ) : (
                        <>
                          {document.request.pdfTemplateId && (
                            <Button
                              variant={mobile ? 'text' : 'outlined'}
                              color={mobile ? 'primary' : 'default'}
                              className={classes.action}
                              startIcon={<FindInPageOutlinedIcon />}
                              onClick={() => openPreview(document)}
                            >
                              {!mobile && t('general_preview')}
                            </Button>
                          )}
                          {document.request.pdfFileId && (
                            <Button
                              variant={mobile ? 'text' : 'outlined'}
                              color={mobile ? 'primary' : 'default'}
                              className={classes.action}
                              //className={classes.generate}
                              startIcon={
                                document.loading ? (
                                  <CircularProgress size={20} />
                                ) : mobile ? (
                                  <SystemUpdateIcon />
                                ) : (
                                  <GetAppIcon />
                                )
                              }
                              onClick={() => store.downloadDocument(document)}
                              disabled={document.loading}
                            >
                              {!mobile && t('general_download')}
                            </Button>
                          )}
                        </>
                      )
                    }
                    {document.request?.status === DocumentStatus.FAILED && (
                      <Tooltip title={t('pdf_document_error')}>
                        <ErrorIcon htmlColor={EdwardColors.ALERT} />
                      </Tooltip>
                    )}
                  </>
                ),
                cellStyle: {
                  width: mobile ? '5%' : '10%',
                },
              },
            ]}
            detailPanel={[
              {
                render: ({ rowData }) => (
                  <Message
                    style={{ display: rowData.request?.waitingTooLong ? 'flex' : 'none' }}
                    id={`reports_waitingTooLong_${rowData.id}`}
                    message={t('accountDetail_reports_waitingTooLong')}
                    variant={MessageVariant.LIGHT}
                    permanent
                  />
                ),
              },
            ]}
            onRowClick={(event, rowData, togglePanel) => {
              togglePanel!();
            }}
          />
        )}
        <PdfPreview
          open={dialogOpen}
          handleDownload={openDocument ? () => store.downloadDocument(openDocument) : undefined}
          handleClose={() => {
            setDialogOpen(false);
            setOpenDocument(null);
          }}
          openDocument={openDocument}
          renderTemplate={
            openDocument
              ? (afterLoad: undefined) => (
                  <Statement
                    rootStore={rootStore}
                    accountId={accountId}
                    preview
                    type={openDocument.type === 'MONTH' ? 'MONTHLY_STATEMENT' : 'YEARLY_STATEMENT'}
                    //date={sqlDateToLabel(openDocument.date, 'yyyy-MM')}
                    docId={openDocument.request?.pdfTemplateId}
                    afterLoad={afterLoad}
                    onError={() => rootStore.stores.uiStore.stopLoading('pdfPreview')}
                  />
                )
              : () => null
          }
        />
      </CustomPaper>
    </Container>
  );
};

export default compose(inject('rootStore'), observer)(Reports);
